import { useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, Card, Grid, TextField } from "@mui/material";

import { Page, ScreenHeader } from "@APP/components";
import { hideLoader, showLoader } from "@APP/redux";
import { formatErrorMessage } from "@APP/utils";
import { getOrganizationUsers, searchOrganizationById } from "@APP/services/api";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { OrganizationDetail, User } from "@APP/types";

import OrganizationDetails from "./OrganizationDetails";
import OrganizationUsersList from "./OrganizationUsersList";

export const GDPRDashboardView = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const handleErrorCodes = useHandleErrorCodes();

  const [orgId, setOrgId] = useState("");
  const [organizationData, setOrganizationData] = useState<OrganizationDetail | null>(null);
  const [usersData, setUsersData] = useState<User[]>([]);
  const [, setUsersMeta] = useState<{ totalPages: number; totalItems: number } | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchOrganizationData = async (searchOrgId: string) => {
    try {
      dispatch(showLoader());
      setHasSearched(true);

      // Fetch organization details
      const orgData = await searchOrganizationById(searchOrgId);
      setOrganizationData(orgData);

      // Fetch users for the organization
      const usersResponse = await getOrganizationUsers(searchOrgId);
      setUsersData(usersResponse.data || []);
      setUsersMeta(usersResponse.meta);
    } catch (error: any) {
      const errorData = error?.response?.data;

      // If no error code exists, show generic message
      if (!errorData?.errorCode) {
        alert.open(
          "Error",
          "Unable to retrieve organisation details. Please try again or contact support if the issue persists.",
          [{ text: "Okay" }],
        );
      } else {
        // If error code exists, try to handle it
        const isHandled = handleErrorCodes(errorData.errorCode);

        if (!isHandled) {
          const errorMessage = formatErrorMessage(
            error,
            "Unable to retrieve organisation details. Please try again or contact support if the issue persists.",
          );
          alert.open("Error", errorMessage, [{ text: "Okay" }]);
        }
      }

      setOrganizationData(null);
      setUsersData([]);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSearch = () => {
    if (orgId.trim()) {
      fetchOrganizationData(orgId.trim());
    }
  };

  const handleOrganizationUpdate = async () => {
    // Refresh organization data after update
    if (orgId) {
      await fetchOrganizationData(orgId.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Page title="GDPR Dashboard">
      <ScreenHeader title="GDPR Dashboard" />
      <Card elevation={4}>
        <Box p={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  fullWidth
                  label="Enter organisation id"
                  variant="outlined"
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Search by Organisation ID"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  disabled={!orgId.trim()}
                  style={{ minWidth: "120px" }}>
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {hasSearched && organizationData && (
        <>
          <Box my={4}>
            <OrganizationDetails
              organizationData={organizationData}
              onUpdate={handleOrganizationUpdate}
            />
          </Box>

          <Box my={4}>
            <OrganizationUsersList
              usersData={usersData}
              orgId={orgId}
              fetchOrganizationData={fetchOrganizationData}
            />
          </Box>
        </>
      )}

      {hasSearched && !organizationData && (
        <Box my={4}>
          <Card elevation={4}>
            <Box p={4} textAlign="center">
              No organisation found for the given ID.
            </Box>
          </Card>
        </Box>
      )}
    </Page>
  );
};

export default GDPRDashboardView;
